/**
 * Экран подписания (п. 6.7 ТЗ).
 *
 * Форма DocuSeal встраивается в iframe по персональной ссылке /s/{slug} — абонент
 * не уходит на сторонний сайт и не вводит email. Iframe выбран вместо
 * web-компонента `<docuseal-form>` осознанно: тот подключается скриптом с
 * cdn.docuseal.com, а self-hosted установка должна работать без внешних CDN —
 * особенно на планшете со слабой связью.
 *
 * Факт подписания фиксирует webhook на backend. Экран сам опрашивает статус:
 * событие 'completed' из iframe до нас не доходит (разные origin), а webhook
 * может задержаться или не дойти вовсе. Поллинг здесь — не костыль, а
 * единственный надёжный источник истины для интерфейса.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, api } from '../api/client'
import type { Application, SigningSession } from '../api/types'
import { Alert, Button, Loading, Steps } from '../components/ui'
import { listFields } from '../lib/format'

// Пока абонент расписывается, статус проверяем часто; после — реже.
const POLL_INTERVAL_MS = 4000

interface Props {
  application: Application
  onSigned: (application: Application) => void
  onBack: () => void
}

export function SignScreen({ application, onSigned, onBack }: Props) {
  const [session, setSession] = useState<SigningSession | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const [notReady, setNotReady] = useState<{ fields: string[]; consents: string[] } | null>(null)
  const [starting, setStarting] = useState(true)
  const [checking, setChecking] = useState(false)
  const finished = useRef(false)

  const complete = useCallback(async () => {
    if (finished.current) return
    finished.current = true
    onSigned(await api.getApplication(application.id))
  }, [application.id, onSigned])

  const start = useCallback(async () => {
    setStarting(true)
    setError(null)
    setNotReady(null)
    try {
      const next = await api.startSigning(application.id)
      setSession(next)
      if (next.signed) await complete()
    } catch (err) {
      const apiError = err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка')
      if (apiError.status === 422) {
        const detail = apiError.detail as
          | { missing_fields?: string[]; missing_consents?: string[] }
          | undefined
        setNotReady({
          fields: detail?.missing_fields ?? [],
          consents: detail?.missing_consents ?? [],
        })
      } else {
        setError(apiError)
      }
    } finally {
      setStarting(false)
    }
  }, [application.id, complete])

  useEffect(() => {
    void start()
  }, [start])

  // Периодический опрос статуса, пока документ не подписан.
  useEffect(() => {
    if (!session || session.signed || session.declined) return
    const timer = window.setInterval(async () => {
      try {
        const next = await api.getSigning(application.id)
        setSession(next)
        if (next.signed) await complete()
      } catch {
        // Сеть отвалилась — молчим: следующий тик попробует снова, а ругаться
        // на каждый неудачный опрос посреди подписания только мешает.
      }
    }, POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [session, application.id, complete])

  const checkNow = async () => {
    setChecking(true)
    setError(null)
    try {
      // refresh тянет статус напрямую из DocuSeal — помогает, если webhook не дошёл.
      const next = await api.refreshSigning(application.id)
      setSession(next)
      if (next.signed) await complete()
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'Неизвестная ошибка'))
    } finally {
      setChecking(false)
    }
  }

  if (notReady) {
    return (
      <>
        <Steps current={5} />
        <div className="card">
          <h2>Заявка не готова к подписанию</h2>
          <Alert kind="error" title="Нужно вернуться и дозаполнить">
            {notReady.fields.length > 0 && (
              <div>Не заполнено: {listFields(notReady.fields)}.</div>
            )}
            {notReady.consents.length > 0 && <div>Не зафиксированы согласия абонента.</div>}
          </Alert>
          <div className="actions">
            <Button variant="secondary" onClick={onBack}>
              Назад к форме
            </Button>
            <Button onClick={start}>Проверить снова</Button>
          </div>
        </div>
      </>
    )
  }

  if (starting) {
    return (
      <>
        <Steps current={5} />
        <div className="card">
          <Loading text="Готовим договор к подписанию…" />
        </div>
      </>
    )
  }

  return (
    <>
      <Steps current={5} />
      <div className="card">
        <h2>Подписание договора</h2>
        <p className="subtitle">
          Договор {application.contract_number ?? ''} — абонент подписывается пальцем или
          стилусом прямо на экране.
        </p>

        {error && (
          <Alert
            kind="error"
            title={error.retryable ? 'Не удалось связаться с сервером' : 'Ошибка подписания'}
            onRetry={error.retryable ? start : undefined}
            onDismiss={() => setError(null)}
          >
            {error.message}
          </Alert>
        )}

        {session?.declined && (
          <Alert kind="error" title="Абонент отказался от подписания">
            {session.decline_reason ?? 'Причина не указана.'} Оформите подключение на бумаге
            либо создайте новую заявку.
          </Alert>
        )}

        {session?.embed_url && !session.declined && (
          <>
            <iframe
              className="sign-frame"
              src={session.embed_url}
              title="Форма подписания договора"
              // camera/microphone не нужны, а вот доступ к формам и скриптам — да.
              allow="clipboard-write"
            />
            <div className="actions">
              <Button variant="secondary" onClick={onBack}>
                К данным заявки
              </Button>
              <Button variant="secondary" onClick={checkNow} busy={checking}>
                Проверить статус
              </Button>
            </div>
            <p className="subtitle" style={{ marginTop: 12, marginBottom: 0 }}>
              После росчерка нажмите кнопку подтверждения в форме. Экран сам перейдёт
              дальше, когда подпись дойдёт до сервера. Если связь прервалась — вернитесь на
              этот экран позже, подписание продолжится с того же места.
            </p>
          </>
        )}

        {!session?.embed_url && !error && (
          <Alert kind="error" title="Ссылка на подписание не получена" onRetry={start}>
            DocuSeal не вернул форму подписания. Проверьте, что сервис доступен, и повторите.
          </Alert>
        )}
      </div>
    </>
  )
}
